import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UPLOAD_PREFIX } from '../../config/appConfig';
import { User } from 'lucide-react';
import PropTypes from 'prop-types';
import NavigationButtons from '../common/NavigationButtons';
import BookingComponent from '../doctor/BookingComponent';

const PatientDoctorDetail = ({ doctor }) => {
  const navigate = useNavigate();
  const [showFallback, setShowFallback] = useState(false);

  if (!doctor) {
    return (
      <div className="p-6 text-center">
        <p>Professionista non trovato</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-purple-600 text-white rounded"
        >
          Torna indietro
        </button>
      </div>
    );
  }

  const renderProfileImage = () => {
    if (doctor.profile_picture && !showFallback) {
      return (
        <img
          src={`${UPLOAD_PREFIX}/storage/${doctor.profile_picture}`}
          alt={doctor.name}
          className="w-20 h-20 rounded-full object-cover mr-4"
          onError={() => setShowFallback(true)}
        />
      );
    }
    return (
      <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mr-4">
        <User className="w-10 h-10 text-purple-600" />
      </div>
    );
  };

  return (
    <div className="p-6 bg-[#FAF4EB]">

    <NavigationButtons listPath="/patient/dashboard" />
    
    <div className="flex flex-col md:flex-row bg-white rounded-lg shadow-md overflow-hidden">
        <div className="md:w-2/3 p-6">
          <div className="flex items-center mb-4">
            {renderProfileImage()}
            <div>
              <h1 className="text-2xl font-bold">{doctor.name}</h1>
            </div>
          </div>

          {doctor.specialization && (
            <p className="text-gray-600 mb-4">{doctor.specialization}</p>
          )}

          <h2 className="text-xl font-semibold mb-2">Profilo personale</h2>
          <p className="text-gray-700 mb-4">{doctor.biography || 'Nessuna biografia disponibile'}</p>

          {doctor.tags?.length > 0 && (
            <>
              <h2 className="text-xl font-semibold mb-2">Aree di intervento</h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {doctor.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </>
          )}

          {doctor.professional_story && (
            <>
              <h2 className="text-xl font-semibold mb-2">Storia Professionale</h2>
              <p className="text-gray-700 mb-4">{doctor.professional_story}</p>
            </>
          )}

          {doctor.services?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-6">Servizi offerti</h3>
              <div className="space-y-4">
                {doctor.services.map((service) => (
                  <details key={service.id} className="bg-white rounded-lg shadow-sm group">
                    <summary className="p-4 cursor-pointer list-none">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{service.name}</div>
                        <div className="flex gap-4 items-center">
                          <span className="font-semibold text-purple-600">{service.price}€</span>
                          <span className="text-gray-500">{service.duration} min</span>
                          <svg className="w-5 h-5 transition-transform group-open:rotate-180"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                          </svg>
                        </div>
                      </div>
                    </summary>
                    <div className="p-4 pt-3 text-sm text-gray-600 border-t">
                      {service.description}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="md:w-1/3 bg-purple-100 p-6">
          <h2 className="text-xl font-semibold mb-4">Prenota una visita</h2>

          <div className="mb-4">
            <BookingComponent 
              services={doctor.services} 
              doctorId={doctor.id || doctor.user_id} 
              onBookingConfirm={(appointmentData) => {
                navigate('/patient/bookingsummary', { 
                  state: { 
                    doctor, 
                    appointmentData 
                  }
                });
              }} 
            />         
          </div>
        </div>
      </div>
    </div>
  );
};


PatientDoctorDetail.propTypes = {
  doctor: PropTypes.object.isRequired
};

export default PatientDoctorDetail;